import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api from '../../api/axios';
import VerificationDocumentsTable from './VerificationDocumentsTable';
import ActionMenu from '../ActionMenu';

const DOC_LABELS = {
  gst: 'GST Certificate',
  cin: 'Certificate of Incorporation',
  udyam: 'Udyam / Udyog Aadhaar',
  pan: 'PAN Card',
  aadhaar: 'Personal Aadhaar',
  license: 'Company License',
};

function UploadDocumentModal({ apiPrefix, employer, onClose, onUploaded }) {
  const [docType, setDocType] = useState('gst');
  const [docNumber, setDocNumber] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Please choose a file to upload.'); return; }
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('document_type', docType);
      if (docNumber) fd.append('document_number', docNumber);
      fd.append('file', file);

      await api.post(`${apiPrefix}/employers/${employer.id}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      onUploaded();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
       <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-lg bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">Upload Document</h3>
            <p className="text-xs text-gray-400">{employer.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          {error && (
            <p className="text-red-500 text-sm bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Document Type</label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            >
              {Object.entries(DOC_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Document Number (optional)</label>
            <input
              type="text"
              value={docNumber}
              onChange={(e) => setDocNumber(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">File (PDF, JPG, PNG &mdash; max 5MB)</label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-600 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="flex-1 bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white py-2 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {uploading ? 'Uploading\u2026' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const STATUS_META = {
  pending_verification: { label: 'Pending Verification', cls: 'bg-gray-100 text-gray-600' },
  documents_submitted: { label: 'Documents Submitted', cls: 'bg-[#FFFBEB] text-[#F59E0B]' },
  under_review: { label: 'Under Review', cls: 'bg-[#EFF6FF] text-[#2563EB]' },
  verified: { label: 'Verified', cls: 'bg-[#ECFDF5] text-[#10B981]' },
  rejected: { label: 'Rejected', cls: 'bg-[#FEF2F2] text-[#EF4444]' },
  resubmit_required: { label: 'Resubmit Required', cls: 'bg-[#FEF2F2] text-[#EF4444]' },
};

const FILTERS = [
  { key: '', label: 'All' },
  { key: 'documents_submitted', label: 'Documents Submitted' },
  { key: 'under_review', label: 'Under Review' },
  { key: 'resubmit_required', label: 'Resubmit Required' },
  { key: 'verified', label: 'Verified' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'pending_verification', label: 'Pending Verification' },
];

const CITY_EDIT_FIELDS = [
  { key: 'name', label: 'Company / Contact Name' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'phone', label: 'Phone' },
  { key: 'city', label: 'City' },
  { key: 'pincode', label: 'Pincode' },
  { key: 'address', label: 'Address', textarea: true },
  { key: 'company_industry', label: 'Industry' },
  { key: 'company_size', label: 'Company Size' },
  { key: 'company_website', label: 'Website' },
  { key: 'company_gst', label: 'Company GST' },
  { key: 'company_about', label: 'About Company', textarea: true },
];

function CityAutocompleteInput({ value, onChange, hasError }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = value.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.post('/ai/suggest-cities', { query: q });
        setSuggestions(res.data?.suggestions || []);
      } catch (err) {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        autoComplete="off"
        className={`w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent ${hasError ? 'border-red-400' : 'border-gray-200'}`}
      />
      {open && (loading || suggestions.length > 0) && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {loading && <p className="px-3 py-2 text-xs text-gray-400">Loading&hellip;</p>}
          {!loading && suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onChange(s); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-[#F5F3FF]"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EditEmployerModal({ apiPrefix, employer, onClose, onSaved }) {
  const [form, setForm] = useState(() =>
    CITY_EDIT_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: employer[f.key] || '' }), {})
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setFieldErrors({});
    try {
      await api.put(`${apiPrefix}/employers/${employer.id}`, form);
      onSaved();
      onClose();
    } catch (err) {
      const resErrors = err.response?.data?.errors;
      if (resErrors) {
        setFieldErrors(Object.fromEntries(Object.entries(resErrors).map(([k, v]) => [k, v[0]])));
        setError('Please fix the highlighted fields.');
      } else {
        setError(err.response?.data?.message || 'Could not save changes. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
          <h3 className="font-bold text-lg bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">Edit Company Details</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          {error && (
            <p className="text-red-500 text-sm bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
          )}
          {CITY_EDIT_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">{f.label}</label>
              {f.textarea ? (
                <textarea
                  value={form[f.key]}
                  onChange={(e) => set(f.key, e.target.value)}
                  rows={3}
                  className={`w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent ${fieldErrors[f.key] ? 'border-red-400' : 'border-gray-200'}`}
                />
              ) : f.key === 'city' ? (
                <CityAutocompleteInput
                  value={form.city}
                  onChange={(v) => set('city', v)}
                  hasError={!!fieldErrors.city}
                />
              ) : (
                <input
                  type={f.type || 'text'}
                  value={form[f.key]}
                  onChange={(e) => set(f.key, e.target.value)}
                  className={`w-full border rounded-lg px-3 py-2 text-sm ${fieldErrors[f.key] ? 'border-red-400' : 'border-gray-200'}`}
                />
              )}
              {fieldErrors[f.key] && <p className="text-red-500 text-xs mt-1">{fieldErrors[f.key]}</p>}
            </div>
          ))}
          <div className="flex gap-2 pt-2 sticky bottom-0 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-600 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white py-2 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Saving\u2026' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EmployerDetailModal({ apiPrefix, employerId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`${apiPrefix}/employers/${employerId}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Could not load company details.');
      }
    })();
  }, [apiPrefix, employerId]);

  const emp = data?.employer;
  const docs = data?.documents || [];

  const rows = emp
    ? [
        ['Mobile', emp.phone],
        ['Email', emp.email],
        ['City', emp.city],
        ['State', emp.state],
        ['Pincode', emp.pincode],
        ['Industry', emp.company_industry],
        ['Company Size', emp.company_size],
        ['Website', emp.company_website],
        ['GST', emp.company_gst],
        ['Official Email', emp.company_official_email],
        ['Registered On', emp.created_at ? new Date(emp.created_at).toLocaleDateString('en-IN') : ''],
      ]
    : [];

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
          <div>
            <h3 className="font-bold text-lg bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
              {emp?.name || 'Company Details'}
            </h3>
            {emp && <StatusBadge status={emp.company_verified_status} />}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        <div className="p-5 space-y-4 text-sm">
          {!data && !error && <p className="text-gray-400">Loading&hellip;</p>}
          {error && <p className="text-red-500">{error}</p>}

          {emp && (
            <>
              <div className="grid grid-cols-2 gap-3">
                {rows.map(([label, val]) => (
                  <div key={label}>
                    <p className="text-[11px] font-semibold uppercase text-gray-400">{label}</p>
                    <p className="text-gray-800 break-words">{val || '\u2014'}</p>
                  </div>
                ))}
              </div>

              <div>
                <p className="text-[11px] font-semibold uppercase text-gray-400">Address</p>
                <p className="text-gray-800">{emp.address || '\u2014'}</p>
              </div>

              <div>
                <p className="text-[11px] font-semibold uppercase text-gray-400">About</p>
                <p className="text-gray-800">{emp.company_about || '\u2014'}</p>
              </div>

              <div>
                <p className="text-[11px] font-semibold uppercase text-gray-400 mb-1">Documents</p>
                {docs.length === 0 ? (
                  <p className="text-gray-400">No documents uploaded.</p>
                ) : (
                  <ul className="space-y-1">
                    {docs.map((d) => (
                      <li key={d.id} className="flex justify-between border-b border-gray-50 pb-1">
                        <span>{DOC_LABELS[d.document_type] || d.document_type}</span>
                        <span className="capitalize text-gray-500">{d.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ActionsMenu({ employer, onReview, reviewLink, onUpload, onEdit }) {
  return (
    <ActionMenu
      phone={employer.phone}
      items={[
        onReview
          ? { label: 'Review', icon: '🔍', tone: 'purple', onClick: onReview }
          : { label: 'Review', icon: '🔍', tone: 'purple', to: reviewLink },
        { label: 'Upload', icon: '⬆️', tone: 'blue', onClick: onUpload },
        { label: 'Edit', icon: '✏️', tone: 'amber', onClick: onEdit },
      ]}
    />
  );
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending_verification;
  return (
    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function PaginationBar({ total, page, pageSize, onPage, onPageSize, className = '' }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const cur = Math.min(page, pages);
  const from = total === 0 ? 0 : (cur - 1) * pageSize + 1;
  const to = Math.min(cur * pageSize, total);
  const nums = [];
  for (let p = Math.max(1, cur - 2); p <= Math.min(pages, cur + 2); p++) nums.push(p);
  const btn = 'px-3 py-1.5 rounded-lg text-xs font-semibold border disabled:opacity-40';
  return (
    <div className={`flex items-center justify-between flex-wrap gap-3 text-sm text-gray-600 ${className}`}>
      <div className="flex items-center gap-3">
        <span>Showing {from}–{to} of {total}</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          className="border rounded-lg px-2 py-1 text-xs"
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-1">
        <button className={btn} disabled={cur === 1} onClick={() => onPage(1)}>«</button>
        <button className={btn} disabled={cur === 1} onClick={() => onPage(cur - 1)}>Prev</button>
        {nums.map((p) => (
          <button
            key={p}
            onClick={() => onPage(p)}
            className={`${btn} ${p === cur ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-[#7C3AED]' : 'bg-white'}`}
          >
            {p}
          </button>
        ))}
        <button className={btn} disabled={cur === pages} onClick={() => onPage(cur + 1)}>Next</button>
        <button className={btn} disabled={cur === pages} onClick={() => onPage(pages)}>»</button>
      </div>
    </div>
  );
}

/**
 * apiPrefix: '/agent/verification' or '/admin/verification'
 * detailPathPrefix: '/agent/verification' or '/admin/verification' (route base for links)
 */
function VerificationPanelList({ apiPrefix, detailPathPrefix, onSelectEmployer, allowRedecide = false }) {
  const [view, setView] = useState('employers'); // 'employers' | 'documents'
  const [employers, setEmployers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [uploadTarget, setUploadTarget] = useState(null); // employer object, or null
  const [editTarget, setEditTarget] = useState(null);
  const [detailId, setDetailId] = useState(null); 

    const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    setPage(1);
  }, [search, filter, pageSize]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await api.get(`${apiPrefix}/employers`, { params: filter ? { status: filter } : {} });
      setEmployers(res.data);
      setSelectedIds([]);
    } catch (err) {
      console.error('Failed to load employers for verification', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [apiPrefix, filter]);

  useEffect(() => {
    if (view === 'employers') load();
  }, [load, view]);

  const q = search.trim().toLowerCase();
  const visibleEmployers = q
    ? employers.filter((e) => {
        const name = (e.name || '').toLowerCase();
        const email = (e.email || '').toLowerCase();
        const gst = (e.company_gst || '').toLowerCase();
        return name.includes(q) || email.includes(q) || gst.includes(q);
      })
    : employers;

    const pagedEmployers = visibleEmployers.slice((page - 1) * pageSize, page * pageSize);

  const allVisibleSelected = visibleEmployers.length > 0 && visibleEmployers.every((e) => selectedIds.includes(e.id));

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleEmployers.some((e) => e.id === id)));
    } else {
      const visibleIds = visibleEmployers.map((e) => e.id);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <div className="bg-[#FDF5FF] -m-6 p-6 min-h-screen">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
 <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">Employer Verification</h2>
      </div>
      <p className="text-gray-500 text-sm mb-4">Review submitted documents and verify employers.</p>

      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setView('employers')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
         view === 'employers'
  ? 'bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white rounded-full'
  : 'bg-white border border-gray-300 text-gray-600 hover:border-[#7C3AED] hover:text-[#7C3AED] rounded-full'
          }`}
        >
          By Employer
        </button>
        <button
          onClick={() => setView('documents')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
 view === 'documents'
  ? 'bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white rounded-full'
  : 'bg-white border border-gray-300 text-gray-600 hover:border-[#7C3AED] hover:text-[#7C3AED] rounded-full'
          }`}
        >
          All Documents
        </button>
      </div>

      {view === 'documents' && (
        <VerificationDocumentsTable apiPrefix={apiPrefix} allowRedecide={allowRedecide} />
      )}

      {view === 'employers' && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-sm">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by company, email, or GST..."
                className="w-full pl-9 pr-8 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                  aria-label="Clear search"
                >
                  &times;
                </button>
              )}
            </div>
            {q && (
              <span className="text-xs text-gray-400">
                {visibleEmployers.length} of {employers.length} shown
              </span>
            )}
            {selectedIds.length > 0 && (
              <span className="text-xs font-semibold text-[#7C3AED] bg-[#F5F3FF] px-2.5 py-1 rounded-full">
                {selectedIds.length} selected
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
filter === f.key
  ? 'bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white border-transparent'
  : 'bg-white text-gray-600 border-gray-300 hover:border-[#7C3AED]'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

                    <PaginationBar
            className="mb-3"
            total={visibleEmployers.length}
            page={page}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={setPageSize}
          />

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto min-h-[320px]">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 text-xs uppercase border-b border-gray-100 bg-gray-50/60">
                  <th className="px-5 py-3 font-medium w-10">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAll}
                      disabled={visibleEmployers.length === 0}
                      aria-label="Select all"
                    />
                  </th>
                  <th className="px-5 py-3 font-medium">Company</th>
                  <th className="px-5 py-3 font-medium">Email</th>
                  <th className="px-5 py-3 font-medium">Mobile</th>
                  <th className="px-5 py-3 font-medium">GST</th>
                  <th className="px-5 py-3 font-medium">Documents</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium sticky right-0 z-10 bg-gray-50 shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.12)]">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="py-6 text-center text-gray-400">Loading&hellip;</td></tr>
                )}
                {!loading && loadError && (
                  <tr><td colSpan={8} className="py-6 text-center text-red-500">
                    Could not load employers. <button onClick={load} className="underline">Retry</button>
                  </td></tr>
                )}
                {!loading && !loadError && visibleEmployers.length === 0 && (
                  <tr><td colSpan={8} className="py-6 text-center text-gray-400">
                    {q ? 'No employers match your search.' : 'No employers found.'}
                  </td></tr>
                )}
                {!loading && !loadError && pagedEmployers.map((e) => (
                  <tr key={e.id} className="border-t border-gray-50 hover:bg-purple-50/30 transition-colors">
                    <td className="px-5 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(e.id)}
                        onChange={() => toggleSelectOne(e.id)}
                        aria-label={`Select ${e.name}`}
                      />
                    </td>
                    <td className="px-5 py-3">
                      <button
                        type="button"
                        onClick={() => setDetailId(e.id)}
                        className="text-[#7C3AED] hover:underline font-medium text-left"
                      >
                        {e.name}
                      </button>
                    </td>
                    <td className="px-5 py-3">{e.email}</td>
                    <td className="px-5 py-3 whitespace-nowrap">{e.phone || '\u2014'}</td>
                    <td className="px-5 py-3">{e.company_gst || '\u2014'}</td>
                    <td className="px-5 py-3">{e.company_documents_count}</td>
                    <td className="px-5 py-3"><StatusBadge status={e.company_verified_status} /></td>
                    <td className="px-5 py-3 sticky right-0 bg-white shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.12)]">
                   <ActionsMenu
  employer={e}
  onReview={onSelectEmployer ? () => onSelectEmployer(e.id) : null}
  reviewLink={`${detailPathPrefix}/${e.id}`}
  onUpload={() => setUploadTarget(e)}
  onEdit={() => setEditTarget(e)}
/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <PaginationBar
            className="mt-3"
            total={visibleEmployers.length}
            page={page}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={setPageSize}
          />
        </>
      )}

      {uploadTarget && (
        <UploadDocumentModal
          apiPrefix={apiPrefix}
          employer={uploadTarget}
          onClose={() => setUploadTarget(null)}
          onUploaded={load}
        />
      )}

      {detailId && (
        <EmployerDetailModal
          apiPrefix={apiPrefix}
          employerId={detailId}
          onClose={() => setDetailId(null)}
        />
      )}

      {editTarget && (
        <EditEmployerModal
          apiPrefix={apiPrefix}
          employer={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}

export default VerificationPanelList;
